import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { validateTaxConfig, getIndonesianPPh21Example, formatCurrency, type TaxBracketConfig } from '../../../shared/compensation/tax';

const MONTH_NAMES = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

interface TaxBracketEditorProps {
  form: UseFormReturn<any>;
  taxBracketFields: any[];
  appendTaxBracket: (tier: { lower_bound: number; upper_bound: number | null; rate_percent: number }) => void;
  removeTaxBracket: (index: number) => void;
  watchedTaxBrackets?: TaxBracketConfig | any;
}

export function TaxBracketEditor({ 
  form, 
  taxBracketFields, 
  appendTaxBracket, 
  removeTaxBracket,
  watchedTaxBrackets
}: TaxBracketEditorProps) {
  
  const loadExample = () => {
    const example = getIndonesianPPh21Example();
    form.setValue('tax_brackets', example);
  };
  
  const validationErrors = watchedTaxBrackets ? validateTaxConfig(watchedTaxBrackets) : [];
  
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Tax Bracket Configuration</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Configure progressive tax brackets (currency-based, IDR)
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={loadExample}
          >
            Load PPh 21 Example
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => appendTaxBracket({ lower_bound: 0, upper_bound: null, rate_percent: 0 })}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Bracket
          </Button>
        </div>
      </div>

      {/* Fiscal Year Start Month */}
      <FormField
        control={form.control}
        name="tax_brackets.fiscal_year_start_month"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Fiscal Year Start Month</FormLabel>
            <Select
              value={String(field.value ?? 1)}
              onValueChange={(value) => field.onChange(parseInt(value, 10))}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {MONTH_NAMES.map((month) => (
                  <SelectItem key={month.value} value={String(month.value)}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription className="text-xs">
              The month when the fiscal/tax year begins. YTD earnings accumulate from this month. Most countries use January.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {taxBracketFields.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            No tax brackets configured. Click "Load PPh 21 Example" to get started with Indonesian tax rates, or "Add Bracket" to create custom brackets.
          </AlertDescription>
        </Alert>
      )}
      
      {taxBracketFields.map((field, index) => (
        <div key={field.id} className="grid grid-cols-4 gap-3 items-end p-3 border rounded bg-background">
          <FormField
            control={form.control}
            name={`tax_brackets.tiers.${index}.lower_bound`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>From (IDR)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="1000"
                    min={0}
                    placeholder="0"
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name={`tax_brackets.tiers.${index}.upper_bound`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>To (IDR)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="1000"
                    min={0}
                    placeholder="Unlimited"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </FormControl>
                <FormDescription className="text-xs">
                  Leave empty for unlimited
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name={`tax_brackets.tiers.${index}.rate_percent`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rate (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.1"
                    min={0}
                    max={100}
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                </FormControl>
                <FormDescription className="text-xs">
                  e.g., 5 for 5%
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="flex justify-end pb-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => removeTaxBracket(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      
      {/* Validation Feedback */}
      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <strong>Tax Bracket Errors:</strong>
            <ul className="list-disc list-inside mt-1">
              {validationErrors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      
      {validationErrors.length === 0 && taxBracketFields.length > 0 && (
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-xs text-green-800 dark:text-green-400">
            Tax bracket configuration is valid
          </AlertDescription>
        </Alert>
      )}
      
      {/* Indonesian PPh 21 Example Reference */}
      <Alert>
        <AlertDescription className="text-xs space-y-1">
          <p><strong>Indonesian PPh 21 Example:</strong></p>
          <p>• Rp 0 - 60,000,000: 5%</p>
          <p>• Rp 60,000,000 - 250,000,000: 15%</p>
          <p>• Rp 250,000,000 - 500,000,000: 25%</p>
          <p>• Rp 500,000,000 - 5,000,000,000: 30%</p>
          <p>• Above Rp 5,000,000,000: 35%</p>
        </AlertDescription>
      </Alert>
      
      {/* Tax Notes Field */}
      <FormField
        control={form.control}
        name="tax_notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tax Notes (Optional)</FormLabel>
            <FormControl>
              <Input
                placeholder="Additional context (e.g., applicable jurisdiction, special conditions)"
                {...field}
              />
            </FormControl>
            <FormDescription>
              Optional notes about this tax rule
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}